import { Component, OnInit } from '@angular/core';
import { SecurityService } from '../security.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DialogWithCloseButtonComponent } from '../../utils/dialog-with-close-button/dialog-with-close-button.component';
import { CustomerProfile } from '../models/customer-profile.model';
import { environment } from '../../../environments/environment';
import { GenericResponse } from 'src/app/shared/model/generic-response.model';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: false
})
export class RegisterComponent implements OnInit {

  registerForm: FormGroup;
  custProf = new CustomerProfile();
  isLoading = false;
  hidePassword = true;
  hideConfirmPassword = true;

  private appURL = environment.appUrl;
  termsUrl = this.appURL + "/#/terms";
  privacyUrl = this.appURL + "/#/privacy-policy";

  constructor(
    private authSvc: SecurityService,
    private router: Router,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  // Custom validator for password matching
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  get f() { return this.registerForm.controls; }

  registerUser() {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    // Populate CustomerProfile with essential fields
    this.custProf.firstName = this.f['firstName'].value?.trim();
    this.custProf.lastName = this.f['lastName'].value?.trim();
    this.custProf.loginId = this.f['email'].value?.trim().toLowerCase();
    this.custProf.password = this.f['password'].value;
    this.custProf.confirmPassword = this.f['confirmPassword'].value;

    this.authSvc.registerUser(this.custProf).subscribe({
      next: (res: GenericResponse) => {
        this.isLoading = false;
        if (res && res.ret == -1) {
          this.openDialog({
            title: "Registration Failed",
            message: "An account with this email address already exists. Please sign in or use a different email."
          });
          return;
        }

        this.snackBar.open('Account created successfully! Please verify your email to continue.', 'OK', {
          duration: 6000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // Redirect to email verification
        this.router.navigate(["/verify-email", {
          'loginId': this.custProf.loginId,
          'password': this.custProf.password
        }]);
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Registration error:', err);
        const errorMsg = err?.error?.message || "An unexpected error occurred during registration. Please try again.";
        this.openDialog({ title: "Registration Error", message: errorMsg });
      }
    });
  }

  openDialog(data: { title: string, message: string }) {
    this.dialog.open(DialogWithCloseButtonComponent, { data: data });
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}
